/**
 * ManualScreeningList Component
 * 
 * List of manual screening questions with template suggestions
 */

import React, { useMemo } from 'react';
import { Typography, Button, Card, List, Row, Col } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { HR_SCREENING_SUGGESTIONS, SuggestionChip } from '../utils/suggestions';
import ManualScreeningCard from './ManualScreeningCard';
import { ManualScreeningQuestionState } from '../utils/formTransformers';

interface ManualScreeningListProps {
  questions: ManualScreeningQuestionState[];
  onEdit: (question: ManualScreeningQuestionState) => void;
  onDelete: (id: string | number) => void;
  onAddQuestion?: () => void;
  onSelectSuggestion?: (suggestion: string) => void;
  existingQuestions?: string[];
  isEditMode?: boolean; // Whether in edit job post mode
  readOnly?: boolean; // Whether the entire list is read-only (no add/edit/delete)
}

const ManualScreeningList: React.FC<ManualScreeningListProps> = ({
  questions,
  onEdit,
  onDelete,
  onAddQuestion,
  onSelectSuggestion,
  existingQuestions = [],
  isEditMode = false,
  readOnly = false,
}) => {
  // Get all available templates (filter out already selected ones)
  const availableTemplates = useMemo(() => {
    return HR_SCREENING_SUGGESTIONS.filter(suggestion => {
      const normalized = suggestion.text.trim().toLowerCase();
      return !existingQuestions.some(
        q => q.trim().toLowerCase() === normalized
      );
    });
  }, [existingQuestions]);

  const handleAddSuggestion = (suggestion: SuggestionChip) => {
    if (!onSelectSuggestion) return;
    onSelectSuggestion(suggestion.text);
  };

  // Split templates into columns (5 items per column)
  const ITEMS_PER_COLUMN = 5;
  const column1 = availableTemplates.slice(0, ITEMS_PER_COLUMN);
  const column2 = availableTemplates.slice(ITEMS_PER_COLUMN, ITEMS_PER_COLUMN * 2);
  const column3 = availableTemplates.slice(ITEMS_PER_COLUMN * 2);

  // Render a single list item
  const renderListItem = (suggestion: SuggestionChip) => (
    <List.Item
      key={suggestion.id}
      style={{
        padding: '8px 12px',
        cursor: 'pointer',
        borderRadius: '6px',
        marginBottom: 4,
        background: 'var(--ant-color-bg-layout, #f0f2f5)',
        border: '1px solid var(--ant-color-border, #d9d9d9)',
      }}
      onClick={() => handleAddSuggestion(suggestion)}
      actions={[
        <Button
          key="add"
          type="text"
          size="small"
          icon={<PlusOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleAddSuggestion(suggestion);
          }}
          style={{ color: 'var(--ant-color-primary)' }}
        />
      ]}
    >
      <Typography.Text
        style={{ fontSize: 'var(--ant-font-size-sm)' }}
        ellipsis={{ tooltip: suggestion.text }}
      >
        {suggestion.text}
      </Typography.Text>
    </List.Item>
  );

  return (
    <div>
      {/* Questions List */}
      {questions.length > 0 && (
        <div className="manual-screening-list" style={{ marginBottom: 24 }}>
          {questions.map((question, index) => (
            <ManualScreeningCard
              key={question.id}
              question={question}
              index={index}
              onEdit={onEdit}
              onDelete={onDelete}
              isEditMode={isEditMode}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}

      {/* Templates Section - List items in columns */}
      {onSelectSuggestion && availableTemplates.length > 0 && (
        <div style={{ marginTop: questions.length > 0 ? 24 : 0, marginBottom: 16 }}>
          <Typography.Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
            Quick Add Suggestions
          </Typography.Text>
          <Row gutter={[16, 0]}>
            {/* Column 1 */}
            <Col xs={24} sm={24} md={12} lg={8}>
              <List
                size="small"
                dataSource={column1}
                renderItem={renderListItem}
                split={false}
              />
            </Col>
            
            {/* Column 2 */}
            {column2.length > 0 && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <List
                  size="small"
                  dataSource={column2}
                  renderItem={renderListItem}
                  split={false}
                />
              </Col>
            )}

            {/* Column 3 (if more items exist) */}
            {column3.length > 0 && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <List
                  size="small"
                  dataSource={column3}
                  renderItem={renderListItem}
                  split={false}
                />
              </Col>
            )}
          </Row>
        </div>
      )}

      {/* Add Screening Question Button */}
      {onAddQuestion && (
        <Card
          className="add-screening-question-card"
          styles={{ body: { padding: '16px', textAlign: 'center' } }}
          hoverable
          onClick={onAddQuestion}
          style={{ cursor: 'pointer', marginTop: 16 }}
        >
          <Button
            type="text"
            icon={<PlusOutlined />}
            size="large"
            style={{ 
              border: 'none', 
              background: 'transparent'
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 'var(--ant-font-size-base)' }}>
              Add Screening Question
            </Typography.Text>
          </Button>
        </Card>
      )}
    </div>
  );
};

export default ManualScreeningList;
